import { useCallback, useMemo } from 'react';
import { GREEN } from '../../../constants';
import { fmtMoneySmart } from '../../../utils/formatters';

interface RawPoint { date: string | number | Date; pnl: number }

export function toMidnightUTC(d: Date) {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export function buildDailyCumulative(raw: RawPoint[], assumeDelta = true) {
  const map = new Map<number, number>();
  for (const r of raw) {
    const ts = typeof r.date === 'number' ? toMidnightUTC(new Date(r.date)) : toMidnightUTC(new Date(r.date));
    map.set(ts, (map.get(ts) ?? 0) + r.pnl);
  }
  const keys = [...map.keys()].sort((a, b) => a - b);
  if (keys.length === 0) return [] as { ts: number; value: number }[];
  const start = keys[0], end = keys[keys.length - 1];
  const day = 24 * 60 * 60 * 1000;
  const out: { ts: number; value: number }[] = [];
  let cum = 0;
  for (let ts = start; ts <= end; ts += day) {
    const v = map.get(ts) ?? 0;
    cum = assumeDelta ? (cum + v) : (map.has(ts) ? v : cum);
    out.push({ ts, value: cum });
  }
  return out;
}

/**
 * Hook that returns all settings and memoized props needed to render a "P&L" style time-series chart.
 * Use in other widgets to ensure consistent margins, ticks, formatters and tooltip behavior.
 */
export const usePnlChartConfig = (dailyData: Array<{ date: string; cum: number }>, isDark: boolean) => {
  const dataMemo = useMemo(() => buildDailyCumulative(dailyData.map(d => ({ date: d.date, pnl: d.cum })), false), [dailyData]);

  const day = 24 * 60 * 60 * 1000;
  const xmin = dataMemo[0]?.ts ?? 0;
  const xmax = dataMemo[dataMemo.length - 1]?.ts ?? 0;

  const ticksMemo = useMemo(() => {
    if (!xmin || !xmax || xmax <= xmin) return [] as number[];
    const span = Math.max(day, xmax - xmin);
    const divisions = 4;
    const step = span / divisions;
    const ticks: number[] = [];
    for (let i = 0; i <= divisions; i++) {
      const raw = xmin + Math.round(i * step);
      const snapped = toMidnightUTC(new Date(raw));
      ticks.push(Math.max(xmin, Math.min(snapped, xmax)));
    }
    return Array.from(new Set(ticks)).sort((a, b) => a - b);
  }, [xmin, xmax]);

  const domainMemo = useMemo(() => [xmin, xmax] as [number, number], [xmin, xmax]);

  const yAxisWidth = 48;
  // Use horizontal padding: right = 15px, left = 5px (move chart closer to left labels)
  // Increase top margin slightly to provide room for top tick labels and prevent clipping.
  const chartMargin = useMemo(() => ({ top: 8, right: 15, bottom: 10, left: 5 }), []);

  const xTickStyle = useMemo(() => ({ fontSize: 12, fontFamily: 'ui-sans-serif, system-ui', fill: isDark ? '#9ca3af' : '#374151', whiteSpace: 'nowrap' }), [isDark]);
  const yTickStyle = useMemo(() => ({ fontSize: 12, fill: isDark ? '#9ca3af' : '#374151' }), [isDark]);
  const axisLineProps = useMemo(() => ({ shapeRendering: 'crispEdges' as const }), []);

  const xTickFormatter = useCallback((ms: number) => {
    const d = new Date(ms);
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${mm}/${dd}`;
  }, []);

  const renderTooltip = useCallback((props: any) => {
    if (props && props.active && props.payload && props.payload.length) {
      const data = props.payload[0];
      const formatted = ['Cumulative: ', fmtMoneySmart(data.value)];
      return (
        <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-lg p-2 text-sm">
          <p className="font-semibold text-zinc-600 dark:text-zinc-300">{props.label}</p>
          <p style={{ color: data.payload?.fill || data.color || GREEN }}>{formatted[1]}</p>
        </div>
      );
    }
    return null;
  }, []);

  const gridColor = isDark ? '#404040' : '#e5e7eb';

  const lineProps = useMemo(() => ({ stroke: GREEN, strokeWidth: 3, strokeLinecap: 'round' as const }), []);
  const referenceLineProps = useMemo(() => ({ stroke: isDark ? '#71717a' : '#52525b', strokeWidth: 2, strokeDasharray: '0' }), [isDark]);

  return {
    dataMemo,
    ticksMemo,
    domainMemo,
    chartMargin,
    yAxisWidth,
    xTickStyle,
    yTickStyle,
    axisLineProps,
    xTickFormatter,
    renderTooltip,
    gridColor,
    lineProps,
    referenceLineProps,
  } as const;
};

export default usePnlChartConfig;
